// ============================================================================
// CURRENCY FORMATTING — shared helpers
// ============================================================================
// Two flavours:
//   formatCurrency(val)       → '₹ 12,345.67'   (booking screens; 2dp)
//   formatCurrencyShort(val)  → '₹12.4K' / '₹3.4Cr'   (dashboards; thousands)

const formatNumberINR = (val: number, fractionDigits = 2): string =>
  val.toLocaleString('en-IN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

/**
 * Booking-screen style: full Indian-grouped value with 2 decimal places.
 * `null` / `undefined` → '-'.
 */
export const formatCurrency = (val?: number | null): string => {
  if (val == null || isNaN(val)) return '-';
  return `₹ ${formatNumberINR(val, 2)}`;
};

/**
 * Dashboard-tile style: compact short form using Indian numbering scale
 * (Thousands, Lakhs, Crores). Uses 1 decimal place when needed; trims
 * trailing `.0`. Negative values keep the sign.
 *
 *   1234        → ₹1.2K
 *   1_50_000    → ₹1.5L     (Indian: 1 lakh = 100K)
 *   3_40_00_000 → ₹3.4Cr    (Indian: 1 crore = 100 lakh)
 */
export const formatCurrencyShort = (val?: number | null): string => {
  if (val == null || isNaN(val)) return '-';

  const sign = val < 0 ? '-' : '';
  const abs = Math.abs(val);

  let scaled: number;
  let suffix: string;

  if (abs >= 1_00_00_000)      { scaled = abs / 1_00_00_000; suffix = 'Cr'; } // ≥ 1 Crore
  else if (abs >= 1_00_000)    { scaled = abs / 1_00_000;    suffix = 'L';  } // ≥ 1 Lakh
  else if (abs >= 1_000)       { scaled = abs / 1_000;       suffix = 'K';  } // ≥ 1K
  else                         return `${sign}₹${formatNumberINR(abs, 0)}`;

  // 1 decimal, trim trailing '.0'
  const text = scaled.toFixed(1).replace(/\.0$/, '');
  return `${sign}₹${text}${suffix}`;
};

/**
 * Number short form (no currency symbol). Useful for KPI tiles like
 * "1.2K bookings" / "3.4L room nights".
 */
export const formatNumberShort = (val?: number | null): string => {
  if (val == null || isNaN(val)) return '-';

  const sign = val < 0 ? '-' : '';
  const abs = Math.abs(val);

  if (abs >= 1_00_00_000) return `${sign}${(abs / 1_00_00_000).toFixed(1).replace(/\.0$/, '')}Cr`;
  if (abs >= 1_00_000)    return `${sign}${(abs / 1_00_000).toFixed(1).replace(/\.0$/, '')}L`;
  if (abs >= 1_000)       return `${sign}${(abs / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return `${sign}${formatNumberINR(abs, 0)}`;
};

/** Percent formatter, defaulting to 1 decimal. */
export const formatPercent = (val?: number | null, fractionDigits = 1): string => {
  if (val == null || isNaN(val)) return '-';
  return `${val.toFixed(fractionDigits)}%`;
};
